import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  GlobeAltIcon,
  ChevronDownIcon,
  CheckIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useWorkspace } from '../contexts/WorkspaceContext';

const useStyles = makeStyles({
  trigger: {
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  brandIcon: {
    color: tokens.colorBrandForeground1,
  },
  initialBadge: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  muted: {
    color: tokens.colorNeutralForeground2,
  },
  check: {
    color: tokens.colorBrandForeground1,
  },
  createItem: {
    color: tokens.colorBrandForeground1,
  },
});

export default function WorkspaceSwitcher({ onCreateWorkspace }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const {
    currentWorkspace,
    currentWorkspaceId,
    userWorkspaces,
    isAllBrandsView,
    switchWorkspace,
    switchToAllBrands,
  } = useWorkspace();

  const handleSwitchWorkspace = (workspaceId) => {
    if (workspaceId === 'ALL_BRANDS') {
      switchToAllBrands();
    } else {
      switchWorkspace(workspaceId);
    }
  };

  const getWorkspaceInitial = (workspace) => workspace.name.charAt(0).toUpperCase();

  const renderWorkspaceIcon = (workspace) =>
    workspace.logoUrl ? (
      <img src={workspace.logoUrl} alt={workspace.name} className="h-5 w-5 rounded object-cover flex-shrink-0" />
    ) : (
      <div
        className={`h-5 w-5 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${styles.initialBadge}`}
        style={workspace.colors?.primary ? { backgroundColor: workspace.colors.primary } : undefined}
      >
        {getWorkspaceInitial(workspace)}
      </div>
    );

  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>
        <button className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors w-full ${styles.trigger}`}>
          {isAllBrandsView ? (
            <>
              <GlobeAltIcon className={`h-5 w-5 ${styles.brandIcon}`} />
              <Text weight="medium">{t('workspaces.allBrands')}</Text>
            </>
          ) : currentWorkspace ? (
            <>
              {renderWorkspaceIcon(currentWorkspace)}
              <Text weight="medium" truncate className="max-w-[150px]">{currentWorkspace.name}</Text>
            </>
          ) : (
            <Text className={styles.muted}>{t('workspaces.loading')}</Text>
          )}
          <ChevronDownIcon className={`h-4 w-4 ml-auto ${styles.muted}`} />
        </button>
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          <MenuItem
            icon={<GlobeAltIcon className={`h-5 w-5 ${styles.brandIcon}`} />}
            onClick={() => handleSwitchWorkspace('ALL_BRANDS')}
          >
            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 text-left">
                <Text weight="medium" block>{t('workspaces.allBrands')}</Text>
                <Caption1 block className={styles.muted}>
                  {t('workspaces.brandsCount', { count: userWorkspaces.length })}
                </Caption1>
              </div>
              {isAllBrandsView && <CheckIcon className={`h-4 w-4 ${styles.check}`} />}
            </div>
          </MenuItem>

          <MenuDivider />

          {userWorkspaces.map((workspace) => (
            <MenuItem
              key={workspace.id}
              icon={renderWorkspaceIcon(workspace)}
              onClick={() => handleSwitchWorkspace(workspace.id)}
            >
              <div className="flex items-center gap-3 w-full">
                <Text weight="medium" truncate className="flex-1 text-left">{workspace.name}</Text>
                {currentWorkspaceId === workspace.id && <CheckIcon className={`h-4 w-4 ${styles.check}`} />}
              </div>
            </MenuItem>
          ))}

          <MenuDivider />

          <MenuItem
            icon={<PlusIcon className="h-5 w-5" />}
            onClick={() => onCreateWorkspace && onCreateWorkspace()}
            className={styles.createItem}
          >
            {t('workspaces.createNewBrand')}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
